import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BrandMark } from "@/components/BrandLogo";
import { useSettings } from "@/context/SettingsContext";
import { EnquireButton } from "@/components/EnquireButton";
import { FeaturedCourses } from "@/components/CourseCard";
import {
  Plane, GraduationCap, Timer, LineChart, BookMarked, ShieldCheck,
  ArrowRight, Sparkles, CheckCircle2,
} from "lucide-react";

const FEATURES = [
  { icon: GraduationCap, title: "6 DGCA Subjects", desc: "Air Navigation, Meteorology, Air Regulations, Technical, RTR — curated to the DGCA syllabus." },
  { icon: Timer, title: "Timed Mock Exams", desc: "Simulate real exams with countdown, auto-submit and shuffled option order." },
  { icon: BookMarked, title: "Smart Revision", desc: "Replay your incorrect and bookmarked questions to lock in concepts." },
  { icon: LineChart, title: "Performance Analytics", desc: "Subject-wise accuracy, best score and attempt history on one dashboard." },
  { icon: ShieldCheck, title: "Server-Side Scoring", desc: "Answer keys stay on the server — never exposed, so your practice is honest." },
  { icon: Sparkles, title: "Clean Three-Option UI", desc: "Exactly A / B / C with instant explanations in practice mode." },
];

export default function Landing() {
  const { settings } = useSettings();
  return (
    <div className="relative overflow-hidden">
      {/* HERO */}
      <section className="relative">
        <div className="hero-grid absolute inset-0 -z-10 opacity-50" />
        <div className="mx-auto max-w-7xl px-4 pb-20 pt-16 sm:px-6 lg:pt-24">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--brand-navy))] backdrop-blur">
                <Plane className="h-3.5 w-3.5" /> DGCA Exam Prep · ATPL/CPL Ready
              </div>
              <h1 className="font-display text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
                {settings.hero_title || <>Your <span className="brand-text-gradient">journey to the cockpit</span> starts with a sharper question bank.</>}
              </h1>
              <p className="mt-6 max-w-xl text-base text-muted-foreground sm:text-lg">
                {settings.hero_subtitle || "PilotYatra is the DGCA preparation platform built around a tight, honest three-option quiz engine, timed mock exams, revision for weak spots, and clear performance analytics."}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button asChild size="lg" className="h-12 bg-[hsl(var(--brand-orange))] px-7 text-base hover:bg-[hsl(var(--brand-orange))]/90" data-testid="hero-cta-register">
                  <Link to="/register">
                    Start preparing free <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="h-12 px-7 text-base" data-testid="hero-cta-courses">
                  <Link to="/courses">Browse courses &amp; Buy Now</Link>
                </Button>
                <EnquireButton size="lg" variant="ghost" className="h-12 px-5 text-base text-[hsl(var(--brand-orange))]" testId="hero-cta-enquire" />
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--brand-orange))]" /> 60+ seeded questions</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--brand-orange))]" /> Instant explanations</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--brand-orange))]" /> Server-verified scoring</span>
              </div>
            </div>

            <div className="relative">
              <div className="brand-gradient relative overflow-hidden rounded-3xl p-6 text-white card-elevated">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <BrandMark className="h-10 w-10" />
                    <div>
                      <div className="font-display text-lg font-bold">Mock · Air Navigation</div>
                      <div className="text-xs text-white/70">Q 3 of 10 · 57:42 remaining</div>
                    </div>
                  </div>
                  <div className="rounded-full bg-[hsl(var(--brand-orange))] px-2.5 py-0.5 text-xs font-semibold">
                    LIVE
                  </div>
                </div>
                <div className="mt-6 text-base font-medium sm:text-lg">
                  Distance 240 NM to be flown at 160 kt groundspeed. Flight time is:
                </div>
                <div className="mt-5 grid gap-3">
                  {[
                    { k: "A", v: "1 h 20 min" },
                    { k: "B", v: "1 h 30 min", correct: true },
                    { k: "C", v: "1 h 45 min" },
                  ].map((o) => (
                    <div
                      key={o.k}
                      className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${
                        o.correct
                          ? "border-[hsl(var(--brand-orange))] bg-white/15"
                          : "border-white/15 bg-white/5"
                      }`}
                    >
                      <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
                        o.correct ? "bg-[hsl(var(--brand-orange))] text-white" : "bg-white/15 text-white"
                      }`}>{o.k}</div>
                      <div className="text-sm">{o.v}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-4 text-xs text-white/70">
                  <span>Marked for review: 1</span>
                  <span>Attempted: 2 / 10</span>
                </div>
              </div>
              <div className="absolute -right-4 -top-4 -z-10 h-40 w-40 rounded-full bg-[hsl(var(--brand-orange))]/25 blur-3xl" />
              <div className="absolute -bottom-6 -left-6 -z-10 h-56 w-56 rounded-full bg-[hsl(var(--brand-navy))]/20 blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-secondary/40 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[hsl(var(--brand-orange))]">What's inside</div>
            <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl">Everything a DGCA aspirant actually needs</h2>
            <p className="mt-4 text-base text-muted-foreground">Three focused learning modes, zero fluff. Built to help you understand, not just memorise.</p>
          </div>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <Card key={f.title} className="border-border/70 transition hover:-translate-y-1 hover:shadow-lg">
                <CardContent className="p-6">
                  <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--brand-navy))]/10 text-[hsl(var(--brand-navy))]">
                    <f.icon className="h-5 w-5" />
                  </div>
                  <div className="font-display text-lg font-bold">{f.title}</div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <FeaturedCourses />

      <section className="brand-gradient py-16 text-white">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 sm:px-6 md:flex-row md:items-center">
          <div>
            <h2 className="font-display text-2xl font-bold sm:text-3xl">Not sure which course is right for you?</h2>
            <p className="mt-2 text-white/80">Tell us about your goals and our aviation counsellors will guide you.</p>
          </div>
          <EnquireButton variant="default" size="lg" className="h-12 bg-[hsl(var(--brand-orange))] px-7 text-base hover:bg-[hsl(var(--brand-orange))]/90" testId="landing-cta-enquire" />
        </div>
      </section>
    </div>
  );
}
